import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Flag } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { STATUS_META, STATUS_ORDER as _SO, PRIORITY_META, priorityOf, priorityRank } from "@/constants/status";

const STATUS_COLOR = Object.fromEntries(_SO.map((k) => [k, STATUS_META[k].pill]));
const STATUS_OPTIONS = _SO.map((k) => ({ key: k, label: STATUS_META[k].label, dot: STATUS_META[k].dot }));

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** For multi-assignee tasks, show the viewer's personal status (matches the
 * badge on TaskCard) so a chip a user just marked complete on their own slot
 * doesn't stay in the "in progress" colour until every co-assignee finishes. */
function statusFor(task, viewerId) {
  const isMulti = (task.assignees || []).length > 1;
  if (isMulti && viewerId) {
    const slot = (task.assignee_details || []).find((a) => a.user_id === viewerId);
    if (slot && slot.status) return slot.status;
  }
  return task.status;
}

function getMonthGrid(year, month) {
  // month 0-indexed
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDow = (first.getDay() + 6) % 7; // Mon=0
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function CalendarView({ tasks, users, onEdit, onAddOnDate, onDropTask, canAdd, currentUserId, onStatusChange, leavesByUser = {} }) {
  const [dragOverKey, setDragOverKey] = useState(null);
  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });

  const cells = useMemo(() => getMonthGrid(cursor.year, cursor.month), [cursor]);

  const tasksByDay = useMemo(() => {
    const map = new Map();
    const isoOf = (dt) => `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
    const dayIso = (yyyyMmDd) => {
      // "YYYY-MM-DD" → local Date at midnight
      const [y, m, d] = yyyyMmDd.slice(0, 10).split("-").map(Number);
      return new Date(y, m - 1, d);
    };
    tasks.forEach((t) => {
      const startRaw = (t.start_date || "").slice(0, 10) || null;
      const endRaw = t.deadline ? new Date(t.deadline) : null;
      if (!startRaw && (!endRaw || isNaN(endRaw.getTime()))) return;
      const endDay = endRaw && !isNaN(endRaw.getTime()) ? new Date(endRaw.getFullYear(), endRaw.getMonth(), endRaw.getDate()) : null;
      const start = startRaw ? dayIso(startRaw) : endDay;
      const end = endDay || start;
      // Walk every day between start and end inclusive and push the task.
      const cur = new Date(start);
      while (cur.getTime() <= end.getTime()) {
        const key = isoOf(cur);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(t);
        cur.setDate(cur.getDate() + 1);
        // Safety: cap at 60 days to avoid an accidental runaway loop
        if ((cur.getTime() - start.getTime()) / 86400000 > 60) break;
      }
    });
    return map;
  }, [tasks]);

  const label = new Date(cursor.year, cursor.month, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  const userNameOf = (uid) => users.find((u) => u.id === uid)?.name || "";

  const shift = (delta) => {
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  return (
    <div className="rounded-xl border border-white/8 bg-surface overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-surface">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => shift(-1)} data-testid="calendar-prev-btn"
            className="text-white/60 hover:text-white hover:bg-white/5">
            <ChevronLeft size={16} />
          </Button>
          <div className="font-display text-lg tracking-tight min-w-[180px] text-center">{label}</div>
          <Button variant="ghost" size="sm" onClick={() => shift(1)} data-testid="calendar-next-btn"
            className="text-white/60 hover:text-white hover:bg-white/5">
            <ChevronRight size={16} />
          </Button>
        </div>
        <Button variant="ghost" size="sm"
          onClick={() => setCursor({ year: now.getFullYear(), month: now.getMonth() })}
          className="text-white/60 hover:text-white hover:bg-white/5">
          Today
        </Button>
      </div>

      <div className="grid grid-cols-7 border-b border-white/5">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-3 py-2 overline border-r border-white/5 last:border-r-0">{w}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map((date, i) => {
          const isToday = date && sameDay(date, now);
          const key = date ? `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` : `blank-${i}`;
          const dayTasks = date ? (tasksByDay.get(key) || []) : [];
          return (
            <div
              key={key}
              onDragOver={(e) => { if (date && canAdd) { e.preventDefault(); setDragOverKey(key); } }}
              onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
              onDrop={(e) => {
                setDragOverKey(null);
                if (!date || !canAdd) return;
                const taskId = e.dataTransfer.getData("text/task-id");
                if (taskId) onDropTask?.(taskId, date);
              }}
              className={`min-h-[110px] border-r border-b border-white/5 last:border-r-0 p-2 ${
                date ? "" : "bg-black/20"
              } ${dragOverKey === key ? "bg-[#E11D2E]/10 ring-1 ring-[#E11D2E]/40" : ""} transition-colors`}
              data-testid={date ? `calendar-day-${date.getDate()}` : undefined}
            >
              {date && (
                <>
                  <div className="flex items-center justify-between mb-1">
                    <div className={`text-xs font-mono ${isToday ? "text-[#E11D2E] font-bold" : "text-white/40"}`}>
                      {date.getDate()}
                    </div>
                    {canAdd && (
                      <button
                        type="button"
                        onClick={() => onAddOnDate?.(date)}
                        data-testid={`calendar-add-${date.getDate()}`}
                        title="Add a task on this date"
                        className="text-[0.7rem] leading-none text-white/30 hover:text-[#E11D2E] px-1 py-0.5 rounded hover:bg-white/5 transition"
                      >+</button>
                    )}
                  </div>
                  <div className="space-y-1 max-h-[220px] overflow-y-auto scrollbar-thin pr-0.5">
                    {dayTasks.map((t) => {
                      const chipStatus = statusFor(t, currentUserId);
                      const isAssignee = !!currentUserId && (t.assigned_to === currentUserId || (t.assignees || []).includes(currentUserId));
                      const canChangeStatus = !!onStatusChange && (canAdd || isAssignee);
                      return (
                        <div
                          key={t.id}
                          className={`w-full flex items-stretch gap-0.5 rounded overflow-hidden ${STATUS_COLOR[chipStatus] || STATUS_COLOR.not_started}`}
                        >
                          {canChangeStatus && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  type="button"
                                  data-testid={`calendar-status-pip-${t.id}`}
                                  title="Change status"
                                  onClick={(e) => e.stopPropagation()}
                                  className="shrink-0 w-3.5 flex items-center justify-center bg-black/25 hover:bg-black/40 transition"
                                >
                                  <span className={`h-2 w-2 rounded-full ${STATUS_OPTIONS.find((s) => s.key === chipStatus)?.dot || "bg-white/40"}`} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-surface border-white/10 text-white min-w-[150px]" align="start">
                                {STATUS_OPTIONS.map((opt) => (
                                  <DropdownMenuItem
                                    key={opt.key}
                                    data-testid={`calendar-status-option-${t.id}-${opt.key}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (opt.key === chipStatus) return;
                                      if (opt.key === "on_hold") {
                                        // Hold reason is OPTIONAL — flip status without asking.
                                        onStatusChange(t, "on_hold", { hold_reason: t.hold_reason || "" });
                                      } else if (opt.key === "incomplete") {
                                        const reason = window.prompt("Reason it stayed incomplete?");
                                        if (!reason?.trim()) return;
                                        onStatusChange(t, "incomplete", { incomplete_reason: reason.trim() });
                                      } else {
                                        onStatusChange(t, opt.key, {});
                                      }
                                    }}
                                    className="text-xs cursor-pointer flex items-center gap-2"
                                  >
                                    <span className={`h-2 w-2 rounded-full ${opt.dot}`} />
                                    {opt.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          <button
                            type="button"
                            draggable={!!canAdd}
                            onDragStart={(e) => e.dataTransfer.setData("text/task-id", t.id)}
                            onClick={() => onEdit?.(t)}
                            title={`${[t.client, t.project_name].filter(Boolean).join(" · ")}${t.client || t.project_name ? " — " : ""}${t.title} — ${userNameOf(t.assigned_to)}${canAdd ? " (drag to reschedule)" : ""}`}
                            data-testid={`calendar-task-${t.id}`}
                            className={`flex-1 min-w-0 text-left text-[0.7rem] px-1.5 py-1 hover:brightness-125 transition ${canAdd ? "cursor-grab active:cursor-grabbing" : ""}`}
                          >
                            {(t.client || t.project_name) && (
                              <div className="text-[0.6rem] uppercase tracking-wider opacity-70 truncate leading-none mb-0.5">
                                <span className="client-line">{t.client}</span>{t.project_name ? ` · ${t.project_name}` : ""}
                              </div>
                            )}
                            <div className="flex items-center gap-1 truncate">
                              {priorityOf(t) !== "normal" && <Flag size={9} fill="currentColor" className={`shrink-0 ${PRIORITY_META[priorityOf(t)].text}`} />}
                              <span className="truncate">{t.title}</span>
                            </div>
                          </button>
                        </div>
                      );
                    })}
                    {dayTasks.length > 8 && (
                      <div className="text-[0.7rem] text-white/50 px-1.5 py-0.5 sticky bottom-0 bg-surface border-t border-white/10">{dayTasks.length} tasks · scroll for more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
